import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight, ShieldCheck, BookOpen, Target, Lock, Mail, Megaphone } from 'lucide-react';
import { Testimonials } from '../components/Testimonials';
import { PaymentLogos } from '../components/PaymentLogos';
const CART_LINK = '[CART_LINK]';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const CTAButton = ({
  className = '',
  onClick
}: {
  className?: string;
  onClick?: () => void;
}) => <motion.button whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} onClick={onClick} className={`group relative flex w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-xl bg-blue-600 px-8 py-5 font-bold text-white shadow-[0_8px_30px_rgb(37,99,235,0.3)] transition-all hover:bg-blue-700 sm:w-auto sm:flex-row sm:gap-3 ${className}`} data-id="element-27">
    <div className="absolute inset-0 flex h-full w-full justify-center [transform:skew(-12deg)_translateX(-100%)] group-hover:duration-1000 group-hover:[transform:skew(-12deg)_translateX(100%)]" data-id="element-28">
      <div className="relative h-full w-8 bg-white/20" data-id="element-29" />
    </div>
    <span className="text-lg sm:text-xl" data-id="element-30">
      Quiero el Método Leaderar ahora — $7 USD
    </span>
    <ArrowRight className="h-6 w-6 transition-transform group-hover:translate-x-1" data-id="element-31" />
  </motion.button>;
const GuaranteeBadge = () => <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm font-medium text-slate-500" data-id="element-32">
    <span className="flex items-center gap-1" data-id="element-33">
      <Lock className="h-4 w-4" data-id="element-34" /> Pago Seguro
    </span>
    <span className="flex items-center gap-1" data-id="element-35">
      <ShieldCheck className="h-4 w-4" data-id="element-36" /> Garantía 7 días
    </span>
    <span className="flex items-center gap-1" data-id="element-37">
      <Zap className="h-4 w-4" data-id="element-38" /> Acceso Inmediato
    </span>
  </div>;
const Zap = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-39">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" data-id="element-40"></polygon>
  </svg>;
export function LandingPage() {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen overflow-hidden bg-slate-50 selection:bg-blue-200 selection:text-blue-900" data-id="element-41">
      {/* HERO SECTION */}
      <section className="relative px-4 pb-20 pt-24 sm:px-6 lg:px-8 lg:pt-32" data-id="element-42">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50 via-slate-50 to-slate-50" data-id="element-43"></div>
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="mx-auto max-w-4xl text-center" data-id="element-44">
          <motion.div variants={fadeIn} className="mb-6 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-1.5 text-sm font-semibold text-blue-800" data-id="element-45">
            <span className="relative flex h-2 w-2" data-id="element-46">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" data-id="element-47"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" data-id="element-48"></span>
            </span>
            Manual de Arquitectura Comercial
          </motion.div>

          <motion.h1 variants={fadeIn} className="mb-8 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl lg:leading-[1.1]" data-id="element-49">
            Construiste tu negocio con las manos vacías.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600" data-id="element-50">
              Ahora construye el sistema que lo sostenga sin ti.
            </span>
          </motion.h1>

          <motion.p variants={fadeIn} className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-slate-600 sm:text-xl" data-id="element-51">
            El Método Leaderar™ — el manual de arquitectura comercial de quien
            quebró un call center a los pocos años de fundarlo, y reconstruyó
            todo desde cero sin un dólar de capital.
          </motion.p>

          <motion.div variants={fadeIn} className="flex flex-col items-center" data-id="element-52">
            <CTAButton onClick={scrollToPricing} data-id="element-53" />
            <GuaranteeBadge data-id="element-54" />
          </motion.div>
        </motion.div>
      </section>

      {/* EL PROBLEMA */}
      <section className="bg-white px-4 py-20 sm:px-6 lg:px-8" data-id="element-55">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mx-auto max-w-3xl" data-id="element-56">
          <motion.h2 variants={fadeIn} className="mb-8 text-3xl font-bold text-slate-900 sm:text-4xl" data-id="element-57">
            Tienes la energía y la ambición.{' '}
            <span className="text-red-600" data-id="element-58">Te falta el mapa.</span>
          </motion.h2>

          <div className="space-y-6 text-lg text-slate-600" data-id="element-59">
            <motion.p variants={fadeIn} data-id="element-60">
              O ya facturas algo, pero tu operación depende al 100% de que tú
              estés ahí todos los días. Si te enfermas, si te vas una semana,
              todo se detiene.
            </motion.p>
            <motion.p variants={fadeIn} data-id="element-61">
              No es falta de esfuerzo. Es que nadie te enseñó a construir un
              sistema — solo a trabajar más duro dentro de uno que no existe.
            </motion.p>
            <motion.p variants={fadeIn} className="rounded-xl bg-slate-50 p-6 font-medium italic text-slate-800 border-l-4 border-blue-600" data-id="element-62">
              Mientras tanto sigues creyendo que necesitas una oficina, un
              equipo grande o capital que no tienes para empezar en serio. Esa
              creencia es la que te está frenando, no la falta de dinero.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* LA SOLUCIÓN */}
      <section className="bg-slate-900 px-4 py-20 text-white sm:px-6 lg:px-8" data-id="element-63">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mx-auto max-w-4xl" data-id="element-64">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center" data-id="element-65">
            <div data-id="element-66">
              <motion.h2 variants={fadeIn} className="mb-6 text-3xl font-bold sm:text-4xl" data-id="element-67">
                El Método Leaderar™
              </motion.h2>
              <div className="space-y-6 text-lg text-slate-300" data-id="element-68">
                <motion.p variants={fadeIn} data-id="element-69">
                  Es el manual de arquitectura comercial escrito por Jefferson
                  Marcano, fundador de Leaderar, después de fundar y perder su
                  primer call center — y reconstruir un negocio recurrente de
                  $10,000 a $15,000 dólares mensuales en dos años, empezando
                  literalmente sin poder pagar el internet.
                </motion.p>
                <motion.p variants={fadeIn} data-id="element-70">
                  No es teoría de escuela de negocios. Es el manual de
                  operaciones que usó para no repetir los mismos errores.
                </motion.p>
                <motion.p variants={fadeIn} className="font-medium text-white" data-id="element-71">
                  Adentro encontrarás los frameworks exactos: el Score
                  Leaderar™, el Ciclo de Prospección Orgánica, el Sistema
                  Nervioso Comercial™ y el Ciclo de Formación Escalable — la
                  arquitectura completa para dejar de perseguir clientes y
                  empezar a construir un sistema que funciona sin tu presencia
                  constante.
                </motion.p>
              </div>
            </div>
            <motion.div variants={fadeIn} className="relative mx-auto w-full max-w-md" data-id="element-72">
              <div className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-700 p-1 shadow-2xl shadow-blue-900/50" data-id="element-73">
                <div className="flex h-full w-full flex-col items-center justify-center rounded-xl bg-slate-900 p-8 text-center" data-id="element-74">
                  <BookOpen className="mb-6 h-16 w-16 text-blue-400" data-id="element-75" />
                  <h3 className="mb-2 text-2xl font-bold" data-id="element-76">
                    El Método Leaderar™
                  </h3>
                  <p className="text-blue-400" data-id="element-77">
                    Manual de Arquitectura Comercial
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* PARA QUIÉN ES & QUÉ PASA SI NO */}
      <section className="bg-slate-50 px-4 py-20 sm:px-6 lg:px-8" data-id="element-78">
        <div className="mx-auto max-w-5xl" data-id="element-79">
          <div className="grid gap-8 md:grid-cols-2" data-id="element-80">
            {/* Para quién es */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200" data-id="element-81">
              <motion.h3 variants={fadeIn} className="mb-6 flex items-center gap-3 text-2xl font-bold text-slate-900" data-id="element-82">
                <Target className="h-6 w-6 text-blue-600" data-id="element-83" />
                Para quién es
              </motion.h3>
              <ul className="space-y-4" data-id="element-84">
                {['El que quiere iniciar su primera agencia o equipo de ventas sin capital de arranque', 'El que ya factura algo, pero su negocio es en realidad un autoempleo disfrazado', 'El que confunde crecer con escalar y necesita entender la diferencia antes de quemarse', 'El que quiere aprender a conseguir su primer cliente sin invertir en publicidad paga', 'El que sabe que su proceso comercial actual está filtrando dinero por falta de sistema'].map((item, i) => <motion.li key={i} variants={fadeIn} className="flex items-start gap-3" data-id="element-85">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-green-500" data-id="element-86" />
                    <span className="text-slate-700" data-id="element-87">{item}</span>
                  </motion.li>)}
              </ul>
              <motion.div variants={fadeIn} className="mt-6 rounded-lg bg-slate-50 p-4 text-sm text-slate-600" data-id="element-88">
                <span className="font-semibold text-slate-900" data-id="element-89">
                  No es para ti si:
                </span>{' '}
                buscas una fórmula mágica sin ejecución, ni si ya tienes un
                equipo grande y procesos documentados — este manual es para
                construir la cimentación, no para quien ya la tiene.
              </motion.div>
            </motion.div>

            {/* Qué pasa si no */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200" data-id="element-90">
              <motion.h3 variants={fadeIn} className="mb-6 flex items-center gap-3 text-2xl font-bold text-slate-900" data-id="element-91">
                <XCircle className="h-6 w-6 text-red-500" data-id="element-92" />
                Qué pasa si sigues sin esto
              </motion.h3>
              <motion.p variants={fadeIn} className="mb-6 text-slate-600" data-id="element-93">
                Nada dramático. Solo esto, cada mes:
              </motion.p>
              <ul className="space-y-4" data-id="element-94">
                {['Sigues siendo el único que puede cerrar una venta, atender un cliente o resolver un problema', 'Sigues creyendo que necesitas más capital cuando lo que te falta es un proceso documentado', 'Sigues intentando la prospección "genérica" mientras otros construyen autoridad real en LinkedIn', 'Sigues operando datos esparcidos en tu cabeza, notas sueltas y la memoria de tu equipo', 'Sigues confundiendo facturar más con haber construido algo que dure'].map((item, i) => <motion.li key={i} variants={fadeIn} className="flex items-start gap-3" data-id="element-95">
                    <XCircle className="mt-1 h-5 w-5 shrink-0 text-red-400" data-id="element-96" />
                    <span className="text-slate-700" data-id="element-97">{item}</span>
                  </motion.li>)}
              </ul>
              <motion.div variants={fadeIn} className="mt-8 font-medium text-slate-900" data-id="element-98">
                Todo eso por no invertir lo que cuesta menos que un café.
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LO QUE RECIBES */}
      <section className="bg-white px-4 py-20 sm:px-6 lg:px-8" data-id="element-99">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="mx-auto max-w-4xl" data-id="element-100">
          <motion.h2 variants={fadeIn} className="mb-12 text-center text-3xl font-bold text-slate-900 sm:text-4xl" data-id="element-101">
            Lo que recibes hoy
          </motion.h2>

          <div className="space-y-6" data-id="element-102">
            {/* Main Product */}
            <motion.div variants={fadeIn} className="relative overflow-hidden rounded-2xl border-2 border-blue-600 bg-white p-6 shadow-xl sm:p-8" data-id="element-103">
              <div className="absolute right-0 top-0 rounded-bl-2xl bg-blue-600 px-4 py-1 text-sm font-bold text-white" data-id="element-104">
                PRODUCTO PRINCIPAL
              </div>
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start" data-id="element-105">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600" data-id="element-106">
                  <BookOpen className="h-8 w-8" data-id="element-107" />
                </div>
                <div data-id="element-108">
                  <h3 className="mb-2 text-xl font-bold text-slate-900" data-id="element-109">
                    El Método Leaderar™ — Manual de Arquitectura Comercial
                  </h3>
                  <p className="text-slate-600" data-id="element-110">
                    Los cimientos sin dinero, la prospección de guerrilla en
                    LinkedIn, el Sistema Nervioso Comercial™, cómo reclutar
                    asesores (no vendedores), y el factor de escalabilidad. La
                    metodología completa.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bonus 1 */}
            <motion.div variants={fadeIn} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-id="element-111">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start" data-id="element-112">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600" data-id="element-113">
                  <Mail className="h-8 w-8" data-id="element-114" />
                </div>
                <div className="flex-1" data-id="element-115">
                  <div className="mb-2 flex flex-wrap items-center gap-3" data-id="element-116">
                    <h3 className="text-xl font-bold text-slate-900" data-id="element-117">
                      BONO 1 — Guía de Cold Email B2B
                    </h3>
                    <span className="rounded-full bg-green-100 px-3 py-0.5 text-sm font-semibold text-green-700" data-id="element-118">
                      Valor: $50 USD → Incluido
                    </span>
                  </div>
                  <p className="text-slate-600" data-id="element-119">
                    Estrategia avanzada de correo frío para generación de
                    oportunidades B2B: segmentación de nichos, arquitectura del
                    mensaje de alta conversión y métricas de control.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bonus 2 */}
            <motion.div variants={fadeIn} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-id="element-120">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start" data-id="element-121">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-purple-100 text-purple-600" data-id="element-122">
                  <Megaphone className="h-8 w-8" data-id="element-123" />
                </div>
                <div className="flex-1" data-id="element-124">
                  <div className="mb-2 flex flex-wrap items-center gap-3" data-id="element-125">
                    <h3 className="text-xl font-bold text-slate-900" data-id="element-126">
                      BONO 2 — Guía de Clase Maestra: Meta Ads
                    </h3>
                    <span className="rounded-full bg-green-100 px-3 py-0.5 text-sm font-semibold text-green-700" data-id="element-127">
                      Valor: $50 USD → Incluido
                    </span>
                  </div>
                  <p className="text-slate-600" data-id="element-128">
                    Modelo de estructuración publicitaria y generación de leads
                    calificados en Meta Ads Manager: estructura de campaña en
                    tres niveles, método AIDA para el copy, y métricas clave de
                    optimización.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* DYNAMIC PRICING OFFER */}
      <section id="pricing" className="bg-slate-900 px-4 py-20 sm:px-6 lg:px-8" data-id="element-129">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="mx-auto max-w-3xl text-center" data-id="element-130">
          <div className="relative overflow-hidden rounded-3xl bg-white p-8 shadow-2xl sm:p-12" data-id="element-131">
            <div className="absolute -right-16 -top-16 h-32 w-32 rounded-full bg-blue-100 blur-3xl" data-id="element-132"></div>
            <div className="absolute -left-16 -bottom-16 h-32 w-32 rounded-full bg-indigo-100 blur-3xl" data-id="element-133"></div>
            <div className="absolute right-4 top-4 z-20 flex h-20 w-20 rotate-12 flex-col items-center justify-center rounded-full bg-red-600 text-white shadow-lg sm:h-24 sm:w-24" data-id="element-134">
              <span className="text-2xl font-black leading-none sm:text-3xl" data-id="element-135">-93%</span>
              <span className="text-[10px] font-bold uppercase tracking-wide" data-id="element-136">Hoy</span>
            </div>

            <div className="relative z-10" data-id="element-137">
              <h2 className="mb-4 text-3xl font-extrabold text-slate-900" data-id="element-138">
                Empieza a construir tu sistema hoy
              </h2>

              <div className="my-8 flex flex-col items-center justify-center gap-2" data-id="element-139">
                <span className="text-lg font-medium text-slate-500 line-through" data-id="element-140">
                  Valor total: $107 USD
                </span>
                <div className="flex items-baseline gap-2" data-id="element-141">
                  <span className="text-6xl font-black tracking-tight text-slate-900" data-id="element-142">
                    $7
                  </span>
                  <span className="text-xl font-bold text-slate-500" data-id="element-143">USD</span>
                </div>
                <span className="rounded-full bg-green-100 px-4 py-1 text-sm font-bold text-green-700" data-id="element-144">
                  Ahorras $100 USD
                </span>
              </div>

              <div className="mb-8 space-y-4 text-left" data-id="element-145">
                <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3" data-id="element-146">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" data-id="element-147" />
                  <span className="font-medium text-slate-700" data-id="element-148">
                    El Método Leaderar™ Completo
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3" data-id="element-149">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" data-id="element-150" />
                  <span className="font-medium text-slate-700" data-id="element-151">
                    Bono: Guía Cold Email B2B
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3" data-id="element-152">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" data-id="element-153" />
                  <span className="font-medium text-slate-700" data-id="element-154">
                    Bono: Masterclass Meta Ads
                  </span>
                </div>
              </div>

              <motion.a href={CART_LINK} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group relative flex w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-xl bg-blue-600 px-8 py-6 font-bold text-white shadow-[0_8px_30px_rgb(37,99,235,0.3)] transition-all hover:bg-blue-700 sm:flex-row sm:gap-3" data-id="element-155">
                <span className="text-xl" data-id="element-156">
                  Quiero el Método + los 2 bonos ahora
                </span>
                <ArrowRight className="h-6 w-6 transition-transform group-hover:translate-x-1" data-id="element-157" />
              </motion.a>

              <div className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-500" data-id="element-158">
                <Lock className="h-4 w-4" data-id="element-159" /> Pago 100% seguro · Acceso inmediato
                por Shopify
              </div>

              <div className="mt-6" data-id="element-160">
                <PaymentLogos data-id="element-161" />
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* RESPALDO Y CONFIANZA */}
      <section className="bg-white px-4 py-20 sm:px-6 lg:px-8" data-id="element-162">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="mx-auto max-w-4xl" data-id="element-163">
          <div className="grid gap-12 md:grid-cols-2 md:items-center" data-id="element-164">
            <motion.div variants={fadeIn} data-id="element-165">
              <h2 className="mb-6 text-3xl font-bold text-slate-900" data-id="element-166">
                Respaldo y Confianza
              </h2>
              <div className="space-y-4 text-slate-600" data-id="element-167">
                <p data-id="element-168">
                  Esto no lo escribió un consultor que nunca vendió. Lo escribió
                  alguien con 12 años en las trincheras de ventas de call
                  centers, que fundó su primer BPO en 2010, lo vio quebrar, y
                  dos años atrás volvió a empezar desde cero — sin oficina, sin
                  capital, preocupado por pagar el internet.
                </p>
                <p data-id="element-169">
                  Hoy esa misma metodología sostiene una operación de $10,000 a
                  $15,000 dólares mensuales recurrentes, con la meta declarada
                  de cruzar el millón de dólares en los próximos meses.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeIn} className="rounded-2xl bg-blue-50 p-8 ring-1 ring-blue-100" data-id="element-170">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white" data-id="element-171">
                <ShieldCheck className="h-6 w-6" data-id="element-172" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-slate-900" data-id="element-173">
                Garantía total de 7 días
              </h3>
              <p className="text-slate-600" data-id="element-174">
                Si el manual no te da claridad sobre cómo estructurar tu
                negocio, te devolvemos el 100%. Sin preguntas.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 px-4 py-20 sm:px-6 lg:px-8" data-id="element-175">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="mx-auto max-w-3xl" data-id="element-176">
          <h2 className="mb-12 text-center text-3xl font-bold text-slate-900" data-id="element-177">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-6" data-id="element-178">
            {[{
            q: '¿Necesito experiencia previa en ventas o call centers?',
            a: 'No. El manual está escrito para quien empieza desde cero y para quien ya tiene un negocio pero opera en caos.'
          }, {
            q: '¿Necesito invertir en herramientas o publicidad para aplicar esto?',
            a: 'El manual enseña prospección orgánica sin presupuesto publicitario. Los bonos de Cold Email y Meta Ads son estrategias complementarias para cuando decidas escalar con inversión.'
          }, {
            q: '¿Cuándo recibo acceso?',
            a: 'Inmediato tras la compra, a través de Shopify.'
          }, {
            q: '¿Y si no me sirve?',
            a: '7 días de garantía total. Sin condiciones.'
          }].map((faq, i) => <motion.div key={i} variants={fadeIn} className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200" data-id="element-179">
                <h4 className="mb-2 text-lg font-bold text-slate-900" data-id="element-180">
                  {faq.q}
                </h4>
                <p className="text-slate-600" data-id="element-181">{faq.a}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* CIERRE */}
      <section className="bg-slate-900 px-4 py-20 text-center text-white sm:px-6 lg:px-8" data-id="element-182">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="mx-auto max-w-3xl" data-id="element-183">
          <motion.h2 variants={fadeIn} className="mb-6 text-3xl font-bold sm:text-4xl" data-id="element-184">
            Cada mes que sigues sin un sistema es un mes más siendo el cuello de
            botella de tu propio negocio.
          </motion.h2>
          <motion.p variants={fadeIn} className="mb-10 text-xl text-slate-300" data-id="element-185">
            Por $7 y el tiempo que te toma leerlo una vez, eso empieza a cambiar
            hoy.
          </motion.p>
          <motion.div variants={fadeIn} className="flex flex-col items-center" data-id="element-186">
            <CTAButton onClick={scrollToPricing} data-id="element-187" />
            <GuaranteeBadge data-id="element-188" />
          </motion.div>
        </motion.div>
      </section>

      {/* SOCIAL PROOF */}
      <Testimonials data-id="element-189" />

      {/* FOOTER */}
      <footer className="bg-slate-950 px-4 py-8 text-center text-sm text-slate-500 sm:px-6 lg:px-8" data-id="element-190">
        <div className="mx-auto max-w-4xl" data-id="element-191">
          <p data-id="element-192">
            © {new Date().getFullYear()} Leaderar. Todos los derechos
            reservados.
          </p>
          <div className="mt-4 flex justify-center gap-4" data-id="element-193">
            <a href="#" className="hover:text-white" data-id="element-194">
              Términos
            </a>
            <a href="#" className="hover:text-white" data-id="element-195">
              Privacidad
            </a>
            <a href="#" className="hover:text-white" data-id="element-196">
              Contacto
            </a>
          </div>
        </div>
      </footer>
    </div>;
}